import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { DemoBadge } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function DemoData() {
  const [users, setUsers] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [purging, setPurging] = useState(false);

  const load = () => api.get("/admin/demo-users").then((r) => setUsers(r.data)).catch(() => toast.error("Could not load demo users"));
  useEffect(() => {
    load();
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const remove = async (u) => {
    if (!window.confirm(`Remove demo account ${u.email} and all its records?`)) return;
    try {
      await api.delete(`/admin/demo-users/${u.id}`);
      toast.success("Demo account removed.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const purgeAll = async () => {
    if (!window.confirm(`Remove ALL ${users.length} demo accounts and their linked records? This is permanent and audit-logged.`)) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/demo-data/purge");
      toast.success(`Demo data removed: ${data.removed?.users ?? 0} accounts and linked records.`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setPurging(false); }
  };

  if (!users) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="demo-loading" />;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="demo-data-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Admin</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="demo-data-heading">Demo Data</h1>
        <p className="text-sm text-slate-500 mt-1">
          Seeded demo accounts for testing. All records are fictional, completely separate from live users, and can be removed here before go-live.
        </p>
      </div>

      {cfg && !cfg.demo_seeding && (
        <p className="bg-red-500/10 border border-red-500/40 text-red-600 text-sm rounded-sm px-4 py-3" data-testid="prod-demo-warning">
          Production environment — demo data is never created automatically here. Removal is permanent and audit-logged.
        </p>
      )}
      {cfg?.demo_seeding && (
        <p className="bg-amber-400/10 border border-amber-400/40 text-amber-700 text-sm rounded-sm px-4 py-3" data-testid="preview-demo-note">
          Preview/test environment — demo data is seeded automatically here for evaluation. Production startup does not seed it.
        </p>
      )}

      {users && users.length > 0 && (
        <Button variant="outline" onClick={purgeAll} disabled={purging}
          className="border-red-400 text-red-600 bg-transparent hover:bg-red-500/10" data-testid="purge-all-demo-btn">
          <Trash2 className="h-4 w-4 mr-1.5" /> {purging ? "Removing..." : `Remove ALL demo data (${users.length} accounts + linked records)`}
        </Button>
      )}

      <div className="space-y-3" data-testid="demo-users-list">
        {users.map((u) => (
          <div key={u.id} className="bg-slate-panel border border-steel rounded-sm p-4 flex items-center justify-between gap-4" data-testid={`demo-user-${u.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <DemoBadge />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-charcoal truncate">{u.first_name} {u.surname}</p>
                <p className="text-xs text-slate-500 truncate">{u.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{u.role.replace(/_/g, " ")}</span>
              <Button variant="outline" size="icon" onClick={() => remove(u)}
                className="border-steel text-red-400 bg-transparent hover:border-red-500 h-9 w-9" data-testid={`demo-delete-${u.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {!users.length && (
          <p className="text-sm text-slate-500 text-center py-10 bg-slate-panel border border-steel rounded-sm" data-testid="no-demo-users">
            No demo accounts remain. The platform is clean for live use.
          </p>
        )}
      </div>
    </div>
  );
}
